import React, {useEffect, useState} from "react"
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography, Alert} from "@mui/material"
import {Cancel as CancelIcon, ExpandMore as ExpandMoreIcon} from "@mui/icons-material"
import {useLocation} from "react-router-dom"

import {NoActivities} from "./NoActivities"
import {ActivitiesList} from "./ActivitiesList"
import {CustomFieldsList} from "./CustomFieldsList"

import {BackToSearch} from "@indebted/components/BackToSearch"
import {api, AccountDetail} from "@indebted/api"
import {LoadingPage} from "@indebted/components/Loading"

function Account({
	account,
	accountDetailsComponent,
}: {
	account: {
		ID: string
		Closed: boolean
		CustomFields?: AccountDetail[]
		CustomFieldsRecordedAt?: string
		CustomFieldsFormVersion?: string
	}
	accountDetailsComponent: React.ReactNode
}) {
	const [activitiesExpanded, setActivitiesExpanded] = React.useState(false)
	const [activitiesComponent, setActivitiesComponent] = useState<JSX.Element>(<LoadingPage />)
	const [detailsExpanded, setDetailsExpanded] = React.useState(true)
	const [customFieldsExpanded, setCustomFieldsExpanded] = React.useState(false)

	useEffect(() => {
		api.account
			.activities(account.ID)
			.then((activities) => {
				const activitiesArray = activities
				if (activitiesArray.Activities.length === 0) {
					setActivitiesComponent(<NoActivities>No activities</NoActivities>)
				} else {
					setActivitiesComponent(<ActivitiesList activities={activitiesArray} />)
				}
			})
			.catch(() => {
				setActivitiesComponent(<NoActivities>Something went wrong</NoActivities>)
			})
	}, [account.ID])

	const location = useLocation()
	const params = new URLSearchParams(location.search)
	const backButton = () => (
		<Box p={3}>
			<Grid container justifyContent="flex-end">
				<BackToSearch backURL={params.get("accountBack") || ""} />
			</Grid>
		</Box>
	)

	const handleExpandDetails = () => {
		setDetailsExpanded(!detailsExpanded)
		setCustomFieldsExpanded(false)
		setActivitiesExpanded(false)
	}
	const detailsSection = () => (
		<Accordion expanded={detailsExpanded} onChange={handleExpandDetails}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body1" color="textSecondary" display="block">
					Details
				</Typography>
			</AccordionSummary>

			<AccordionDetails>{accountDetailsComponent}</AccordionDetails>
		</Accordion>
	)

	// Custom fields are client-defined, so the section exists only for the clients
	// that have a form and only on accounts an upload has actually recorded values
	// for. Everyone else sees the account exactly as before — an empty section
	// would read as "this account is missing something" rather than "not in use".
	const customFields = account.CustomFields ?? []
	const handleExpandCustomFields = () => {
		setCustomFieldsExpanded(!customFieldsExpanded)
		setDetailsExpanded(false)
		setActivitiesExpanded(false)
	}
	const customFieldsSection = () => (
		<Accordion expanded={customFieldsExpanded} onChange={handleExpandCustomFields}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body1" color="textSecondary" display="block">
					Custom Fields
				</Typography>
			</AccordionSummary>

			<AccordionDetails>
				<CustomFieldsList
					customFields={customFields}
					recordedAt={account.CustomFieldsRecordedAt}
					formVersion={account.CustomFieldsFormVersion}
				/>
			</AccordionDetails>
		</Accordion>
	)

	const handleExpandActivities = () => {
		setActivitiesExpanded(!activitiesExpanded)
		setDetailsExpanded(false)
		setCustomFieldsExpanded(false)
	}
	const activitiesSection = () => (
		<Accordion expanded={activitiesExpanded} onChange={handleExpandActivities}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body1" color="textSecondary" display="block">
					Activities
				</Typography>
			</AccordionSummary>

			<AccordionDetails>{activitiesComponent}</AccordionDetails>
		</Accordion>
	)

	return (
		<>
			{backButton()}
			{account.Closed && (
				<Alert severity="error" icon={<CancelIcon />}>
					Account closed for collection
				</Alert>
			)}
			{detailsSection()}
			{customFields.length > 0 && customFieldsSection()}
			{activitiesSection()}
		</>
	)
}

export {Account}
